import React, { useEffect, useState } from "react";
import {
  X, Paperclip, FileText, Send, Trash2, MessageSquare,
  CreditCard, Package, Truck, ClipboardList,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const QUERY_STATUSES = [
  { v: "open", label: "Open", color: "#A3141A" },
  { v: "answered", label: "Answered", color: "#3F6E2C" },
  { v: "waiting_client", label: "Waiting for client", color: "#A6822F" },
  { v: "waiting_supplier", label: "Waiting for supplier", color: "#D4A963" },
  { v: "closed", label: "Closed", color: "#7A6B5F" },
];

const statusMeta = (v) => QUERY_STATUSES.find((s) => s.v === v) || QUERY_STATUSES[0];

function StatusChip({ status }) {
  const m = statusMeta(status);
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 border"
      style={{ color: m.color, borderColor: `${m.color}66`, background: `${m.color}12` }}
    >
      {m.label}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";

function AttachmentChips({ files, onRemove }) {
  if (!files?.length) return null;
  return (
    <ul className="flex flex-wrap gap-2 mt-2">
      {files.map((f) => (
        <li
          key={f.id}
          className="inline-flex items-center gap-2 bg-[#FAF5EC] border border-[#6E1F2C]/20 px-2.5 py-1.5 text-xs text-[#3E2A22]"
        >
          <FileText size={11} className="text-[#6E1F2C]" />
          {f.url ? (
            <a href={f.url} target="_blank" rel="noopener noreferrer" className="max-w-[180px] truncate hover:underline">
              {f.filename || "file"}
            </a>
          ) : (
            <span className="max-w-[180px] truncate">{f.filename || "file"}</span>
          )}
          {onRemove && (
            <button type="button" onClick={() => onRemove(f.id)} className="text-[#7A6B5F] hover:text-[#A3141A]">
              <X size={11} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/* ---------- Tracking form ---------- */
function TrackingForm({ slug, pkg, canEdit, onSaved }) {
  const buildForm = (p) => ({
    payment_made: !!p.payment_made,
    advance_payment_amount: p.advance_payment_amount ?? "",
    payment_date: p.payment_date || "",
    order_placed: !!p.order_placed,
    order_reference: p.order_reference || "",
    delivery_received: !!p.delivery_received,
    expected_delivery_date: p.expected_delivery_date || "",
    scheduled_delivery_date: p.scheduled_delivery_date || "",
    actual_delivery_date: p.actual_delivery_date || "",
    supplier_latest_update: p.supplier_latest_update || "",
    pending_action_supplier: p.pending_action_supplier || "",
    pending_action_client: p.pending_action_client || "",
    pending_action_designer: p.pending_action_designer || "",
    pending_action_tf: p.pending_action_tf || "",
    related_approvals: p.related_approvals || "",
    related_reference: p.related_reference || "",
    notes: p.notes || "",
  });
  const [f, setF] = useState(() => buildForm(pkg));
  const [files, setFiles] = useState(pkg.attachments || []);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setF(buildForm(pkg));
    setFiles(pkg.attachments || []);
  }, [pkg]); // eslint-disable-line

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      const { attachments } = await portalApi.upload(slug, picked, "file");
      setFiles((p) => [...p, ...attachments]);
    } catch (err) { setMsg(err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const body = { ...f, attachment_ids: files.map((x) => x.id) };
      if (body.advance_payment_amount === "") body.advance_payment_amount = null;
      else body.advance_payment_amount = Number(body.advance_payment_amount);
      await portalApi.patchExternalSupply(slug, pkg.id, body);
      setMsg("Saved.");
      onSaved && onSaved();
    } catch (e2) { setMsg(e2.message); }
    finally { setBusy(false); }
  };

  const Section = ({ icon: Icon, title, children }) => (
    <div className="border border-[#6E1F2C]/12 bg-[#FAF5EC]/40 p-4">
      <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] mb-3 inline-flex items-center gap-1.5">
        <Icon size={12} /> {title}
      </p>
      {children}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="vendor-pkg-tracking-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Section icon={CreditCard} title="Payment">
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="vendor-pkg-payment-made"
              className="accent-[#6E1F2C]"
              checked={!!f.payment_made}
              disabled={!canEdit}
              onChange={(e) => set("payment_made", e.target.checked)}
            />
            <span className="text-sm text-[#1C110C]">Payment made</span>
          </label>
          <div className="space-y-2.5">
            <Field label="Advance amount (AED)">
              <input type="number" data-testid="vendor-pkg-advance-amount" className={inputCls} value={f.advance_payment_amount} disabled={!canEdit} onChange={(e) => set("advance_payment_amount", e.target.value)} />
            </Field>
            <Field label="Payment date">
              <input type="date" data-testid="vendor-pkg-payment-date" className={inputCls} value={f.payment_date} disabled={!canEdit} onChange={(e) => set("payment_date", e.target.value)} />
            </Field>
          </div>
        </Section>
        <Section icon={Package} title="Order">
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="vendor-pkg-order-placed"
              className="accent-[#6E1F2C]"
              checked={!!f.order_placed}
              disabled={!canEdit}
              onChange={(e) => set("order_placed", e.target.checked)}
            />
            <span className="text-sm text-[#1C110C]">Order placed</span>
          </label>
          <Field label="Order confirmation / reference no.">
            <input data-testid="vendor-pkg-order-reference" className={inputCls} value={f.order_reference} disabled={!canEdit} onChange={(e) => set("order_reference", e.target.value)} placeholder="PO-2026-014" />
          </Field>
        </Section>
        <Section icon={Truck} title="Delivery">
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="vendor-pkg-delivery-received"
              className="accent-[#6E1F2C]"
              checked={!!f.delivery_received}
              disabled={!canEdit}
              onChange={(e) => set("delivery_received", e.target.checked)}
            />
            <span className="text-sm text-[#1C110C]">Delivery received</span>
          </label>
          <div className="space-y-2.5">
            <Field label="Expected delivery">
              <input type="date" data-testid="vendor-pkg-expected-delivery" className={inputCls} value={f.expected_delivery_date} disabled={!canEdit} onChange={(e) => set("expected_delivery_date", e.target.value)} />
            </Field>
            <Field label="Scheduled delivery">
              <input type="date" data-testid="vendor-pkg-scheduled-delivery" className={inputCls} value={f.scheduled_delivery_date} disabled={!canEdit} onChange={(e) => set("scheduled_delivery_date", e.target.value)} />
            </Field>
            <Field label="Actual delivery">
              <input type="date" data-testid="vendor-pkg-actual-delivery" className={inputCls} value={f.actual_delivery_date} disabled={!canEdit} onChange={(e) => set("actual_delivery_date", e.target.value)} />
            </Field>
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Supplier latest update">
          <textarea data-testid="vendor-pkg-supplier-update" rows={2} className={inputCls} value={f.supplier_latest_update} disabled={!canEdit} onChange={(e) => set("supplier_latest_update", e.target.value)} />
        </Field>
        <Field label="Notes / remarks">
          <textarea data-testid="vendor-pkg-notes" rows={2} className={inputCls} value={f.notes} disabled={!canEdit} onChange={(e) => set("notes", e.target.value)} />
        </Field>
      </div>

      <div className="border border-[#D4A963]/40 bg-[#FAF5EC]/60 p-4">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#A6822F] mb-3 inline-flex items-center gap-1.5">
          <ClipboardList size={12} /> Pending actions
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="From supplier">
            <input data-testid="vendor-pkg-pending-supplier" className={inputCls} value={f.pending_action_supplier} disabled={!canEdit} onChange={(e) => set("pending_action_supplier", e.target.value)} />
          </Field>
          <Field label="From client">
            <input data-testid="vendor-pkg-pending-client" className={inputCls} value={f.pending_action_client} disabled={!canEdit} onChange={(e) => set("pending_action_client", e.target.value)} />
          </Field>
          <Field label="From designer">
            <input data-testid="vendor-pkg-pending-designer" className={inputCls} value={f.pending_action_designer} disabled={!canEdit} onChange={(e) => set("pending_action_designer", e.target.value)} />
          </Field>
          <Field label="From Time Furniture">
            <input data-testid="vendor-pkg-pending-tf" className={inputCls} value={f.pending_action_tf} disabled={!canEdit} onChange={(e) => set("pending_action_tf", e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Related approvals">
          <input data-testid="vendor-pkg-related-approvals" className={inputCls} value={f.related_approvals} disabled={!canEdit} onChange={(e) => set("related_approvals", e.target.value)} placeholder="e.g. Material approval MA-07 signed 12 Jun" />
        </Field>
        <Field label="Related chat / email reference">
          <input data-testid="vendor-pkg-related-reference" className={inputCls} value={f.related_reference} disabled={!canEdit} onChange={(e) => set("related_reference", e.target.value)} placeholder="e.g. WhatsApp 04 Jul, email thread 'cove lighting'" />
        </Field>
      </div>

      <div>
        <div className="flex items-center gap-3 flex-wrap">
          {canEdit && (
            <label className="cursor-pointer inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] px-3 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors">
              <Paperclip size={12} /> {uploading ? "Uploading…" : "Add files / drawings"}
              <input type="file" multiple className="hidden" data-testid="vendor-pkg-file-input" onChange={onPick} />
            </label>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={save}
              disabled={busy || uploading}
              data-testid="vendor-pkg-save"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2 text-[10px] tracking-[0.24em] uppercase disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save tracking"}
            </button>
          )}
          {msg && <p className="text-[11px] text-[#3F6E2C]" data-testid="vendor-pkg-save-msg">{msg}</p>}
        </div>
        <AttachmentChips files={files} onRemove={canEdit ? (id) => setFiles((p) => p.filter((x) => x.id !== id)) : null} />
      </div>
    </div>
  );
}

/* ---------- Query log ---------- */
function QueryLog({ slug, pkg, canEdit }) {
  const [queries, setQueries] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState({ raised_by: "", query: "", status: "open" });
  const [qFiles, setQFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [respondingId, setRespondingId] = useState(null);
  const [respText, setRespText] = useState("");
  const [respBy, setRespBy] = useState("");

  const reload = async () => {
    try {
      const d = await portalApi.listSupplyQueries(slug, pkg.id);
      setQueries(d.queries || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); }, [pkg.id]); // eslint-disable-line

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      const { attachments } = await portalApi.upload(slug, picked, "file");
      setQFiles((p) => [...p, ...attachments]);
    } catch (err) { setMsg(err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addQuery = async () => {
    if (!q.query.trim()) { setMsg("Query details are required."); return; }
    setBusy(true); setMsg("");
    try {
      await portalApi.createSupplyQuery(slug, pkg.id, {
        ...q, query: q.query.trim(),
        attachment_ids: qFiles.map((x) => x.id),
      });
      setQ({ raised_by: "", query: "", status: "open" });
      setQFiles([]); setShowForm(false);
      await reload();
    } catch (e2) { setMsg(e2.message); }
    finally { setBusy(false); }
  };

  const respond = async (id) => {
    if (!respText.trim()) return;
    setBusy(true); setMsg("");
    try {
      await portalApi.patchSupplyQuery(slug, id, { response: respText.trim(), responded_by: respBy || undefined });
      setRespondingId(null); setRespText(""); setRespBy("");
      await reload();
    } catch (e2) { setMsg(e2.message); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await portalApi.patchSupplyQuery(slug, id, { status }); await reload(); } catch { /* ignore */ }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this query log entry?")) return;
    try { await portalApi.deleteSupplyQuery(slug, id); await reload(); } catch { /* ignore */ }
  };

  const term = search.trim().toLowerCase();
  const visible = term
    ? queries.filter((x) =>
        [x.query, x.response, x.raised_by, x.responded_by, x.internal_note, x.client_visible_note]
          .some((v) => (v || "").toLowerCase().includes(term)))
    : queries;

  return (
    <div className="space-y-4" data-testid="vendor-pkg-query-log">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <input
          data-testid="vendor-pkg-query-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search queries & responses…"
          className="flex-1 min-w-[200px] bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
        />
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            data-testid="vendor-pkg-query-add-btn"
            className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-4 py-2 text-[10px] tracking-[0.24em] uppercase"
          >
            <MessageSquare size={12} /> {showForm ? "Cancel" : "Log query"}
          </button>
        )}
      </div>
      {msg && <p className="text-[11px] text-[#A3141A]">{msg}</p>}

      {showForm && (
        <div className="border border-[#6E1F2C]/15 bg-[#FAF5EC]/50 p-4 space-y-3" data-testid="vendor-pkg-query-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Raised by">
              <input data-testid="vendor-pkg-query-raised-by" className={inputCls} value={q.raised_by} onChange={(e) => setQ((p) => ({ ...p, raised_by: e.target.value }))} placeholder="e.g. Client / Supplier – Al Noor Lighting / Designer" />
            </Field>
            <Field label="Status">
              <select data-testid="vendor-pkg-query-status" className={inputCls} value={q.status} onChange={(e) => setQ((p) => ({ ...p, status: e.target.value }))}>
                {QUERY_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Query details">
            <textarea data-testid="vendor-pkg-query-text" rows={2} className={inputCls} value={q.query} onChange={(e) => setQ((p) => ({ ...p, query: e.target.value }))} placeholder="What was asked / clarification needed…" />
          </Field>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="cursor-pointer inline-flex items-center gap-2 border border-[#6E1F2C]/40 text-[#6E1F2C] px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:border-[#6E1F2C]">
              <Paperclip size={11} /> {uploading ? "Uploading…" : "Attach files"}
              <input type="file" multiple className="hidden" data-testid="vendor-pkg-query-file-input" onChange={onPick} />
            </label>
            <button
              type="button"
              onClick={addQuery}
              disabled={busy || uploading}
              data-testid="vendor-pkg-query-submit"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-4 py-2 text-[10px] tracking-[0.24em] uppercase disabled:opacity-60"
            >
              <Send size={11} /> {busy ? "Saving…" : "Save query"}
            </button>
          </div>
          <AttachmentChips files={qFiles} onRemove={(id) => setQFiles((p) => p.filter((x) => x.id !== id))} />
        </div>
      )}

      {visible.length === 0 ? (
        <p className="text-[12px] text-[#7A6B5F] italic" data-testid="vendor-pkg-query-empty">
          {queries.length === 0 ? "No queries logged for this package yet." : "No queries match your search."}
        </p>
      ) : (
        <ul className="space-y-3">
          {visible.map((it) => (
            <li key={it.id} data-testid={`vendor-pkg-query-${it.id}`} className="border border-[#6E1F2C]/12 bg-white p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusChip status={it.status} />
                  <span className="text-[10px] text-[#7A6B5F]">{it.date}</span>
                  {it.raised_by && <span className="text-[10px] text-[#3E2A22]">Raised by <strong>{it.raised_by}</strong></span>}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    <select
                      value={it.status}
                      onChange={(e) => setStatus(it.id, e.target.value)}
                      data-testid={`vendor-pkg-query-status-select-${it.id}`}
                      className="bg-[#FAF5EC] border border-[#6E1F2C]/20 px-2 py-1 text-[10px] text-[#1C110C]"
                    >
                      {QUERY_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                    <button type="button" onClick={() => remove(it.id)} data-testid={`vendor-pkg-query-delete-${it.id}`} className="p-1 text-[#7A6B5F] hover:text-[#A3141A]">
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-sm text-[#1C110C] mt-2 whitespace-pre-wrap">{it.query}</p>
              {it.response ? (
                <div className="mt-2 border-l-2 border-[#3F6E2C] pl-3">
                  <p className="text-[10px] text-[#3F6E2C] uppercase tracking-[0.16em]">
                    Response{it.responded_by ? ` — ${it.responded_by}` : ""}{it.response_date ? ` · ${it.response_date}` : ""}
                  </p>
                  <p className="text-sm text-[#3E2A22] whitespace-pre-wrap">{it.response}</p>
                </div>
              ) : canEdit && (respondingId === it.id ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    data-testid={`vendor-pkg-query-response-text-${it.id}`}
                    rows={2}
                    className={inputCls}
                    value={respText}
                    onChange={(e) => setRespText(e.target.value)}
                    placeholder="Answer given (by WhatsApp / email / meeting)…"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      className="bg-white border border-[#6E1F2C]/20 px-3 py-1.5 text-xs text-[#1C110C]"
                      value={respBy}
                      onChange={(e) => setRespBy(e.target.value)}
                      placeholder="Responded by"
                    />
                    <button type="button" onClick={() => respond(it.id)} disabled={busy} data-testid={`vendor-pkg-query-respond-submit-${it.id}`} className="bg-[#3F6E2C] text-white px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-[#2F5520] disabled:opacity-60">
                      Save response
                    </button>
                    <button type="button" onClick={() => setRespondingId(null)} className="text-[10px] text-[#7A6B5F] uppercase tracking-[0.2em]">Cancel</button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => { setRespondingId(it.id); setRespText(""); }} data-testid={`vendor-pkg-query-respond-btn-${it.id}`} className="mt-2 text-[10px] text-[#6E1F2C] uppercase tracking-[0.2em] underline underline-offset-2 hover:text-[#56151F]">
                  Add response
                </button>
              ))}
              {(it.internal_note || it.client_visible_note) && (
                <div className="mt-2 text-[10px] text-[#7A6B5F] space-y-0.5">
                  {it.internal_note && <p>🔒 Internal: {it.internal_note}</p>}
                  {it.client_visible_note && <p>Client note: {it.client_visible_note}</p>}
                </div>
              )}
              <AttachmentChips files={it.attachments} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Modal shell ---------- */
export default function VendorPackageModal({ slug, packageId, canEdit, onClose, onChanged }) {
  const [pkg, setPkg] = useState(null);
  const [counts, setCounts] = useState(null);
  const [tab, setTab] = useState("tracking");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const d = await portalApi.getExternalSupplyDetail(slug, packageId);
      setPkg(d.external_supply);
      setCounts(d.query_counts);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, [slug, packageId]); // eslint-disable-line

  const openQueries = counts ? (counts.open || 0) + (counts.waiting_client || 0) + (counts.waiting_supplier || 0) : 0;

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="vendor-pkg-modal"
        className="bg-white w-full max-w-[860px] border border-[#6E1F2C]/20 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC] sticky top-0 z-10">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Vendor package</p>
            <h3 className="font-serif-display text-xl text-[#1C110C]">{pkg?.item_group || "…"}</h3>
            {pkg?.vendor_source && <p className="text-[11px] text-[#7A6B5F]">{pkg.vendor_source}</p>}
          </div>
          <button type="button" onClick={onClose} data-testid="vendor-pkg-modal-close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>

        <div className="px-6 pt-4 flex gap-2 border-b border-[#6E1F2C]/10">
          {[
            { v: "tracking", label: "Tracking" },
            { v: "queries", label: `Queries & responses${openQueries ? ` (${openQueries} open)` : ""}` },
          ].map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setTab(t.v)}
              data-testid={`vendor-pkg-tab-${t.v}`}
              className={`px-4 py-2.5 text-[10px] tracking-[0.24em] uppercase border-b-2 transition-colors ${
                tab === t.v ? "border-[#6E1F2C] text-[#6E1F2C]" : "border-transparent text-[#7A6B5F] hover:text-[#1C110C]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {err && <p className="text-xs text-[#A3141A]">{err}</p>}
          {!pkg && !err && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
          {pkg && tab === "tracking" && (
            <TrackingForm slug={slug} pkg={pkg} canEdit={canEdit} onSaved={() => { load(); onChanged && onChanged(); }} />
          )}
          {pkg && tab === "queries" && (
            <QueryLog slug={slug} pkg={pkg} canEdit={canEdit} />
          )}
        </div>
      </div>
    </div>
  );
}
